import { supabase } from '@/shared/lib/supabase';
import { isProjectStatus } from '@/shared/constants/statuses';
import { toDateInputValue } from '@/shared/lib/dates';
import type { AgendaOccurrence, Id, IsoDateTime, ProjectStatus } from '@/shared/types/domain';

/**
 * Única camada da feature `calendar` que conhece o supabase-js
 * (architecture.md §5.1).
 *
 * Ela é curta de propósito: as duas consultas de PROJETO moram em
 * `features/projects/api` (a RPC `agenda_projects` e o `select` de "Meus
 * Projetos"), porque a leitura reduzida também é consumida pela tela de detalhe
 * do projeto e a lista de colunas não pode existir em dois lugares
 * (spec-calendar.md §10). O que sobra aqui é o que é só do calendário: a agenda
 * fixa e a definição estrita de "meu".
 */

/**
 * As ocorrências da agenda fixa no intervalo (spec-fixed-agenda.md §6–7).
 *
 * ⚠️ **A fonte existe e vem VAZIA** enquanto aquela spec não for implementada
 * (spec-calendar.md §3.4). Isto não é um `TODO`: é a costura declarada em vez de
 * prometida. O adaptador, o seletor de fontes e a lista do dia já tratam a
 * população; no dia em que `activity_occurrences` nascer, esta função troca de
 * corpo e **nada na tela muda**. A alternativa — não ter a fonte agora — faria a
 * agenda fixa chegar como uma refatoração de quatro arquivos.
 */
export async function fetchAgendaOccurrences(
  _from: string,
  _to: string,
): Promise<AgendaOccurrence[]> {
  return [];
}

/**
 * Os ministérios em que a pessoa tem vínculo DIRETO de liderança — a metade
 * do "meu" de §3.2 que não é autoria.
 *
 * **Por que não `my_ministry_ids()`**: aquela função devolve, para `overseer`, a
 * filial inteira, e para `admin`, todos os ministérios da igreja. "Meus
 * Projetos" viraria "tudo" justamente para quem mais precisa da distinção. E não
 * é `my_writable_ministry_ids()` tampouco: ela inclui o admin em toda a igreja,
 * pela mesma razão, e ali isso está certo — escrever não é ser dono.
 *
 * `memberships` já é legível pela própria pessoa (`memberships_select`:
 * `profile_id = current_profile_id() or is_admin()`), então o filtro por
 * `profile_id` faz esta consulta devolver o mesmo conjunto para os cinco papéis,
 * inclusive o admin. É a tradução literal do `active_memberships()` da RPC — e é
 * por isso que os dois lados concordam sem uma segunda regra escrita.
 */
export async function fetchMyDirectMinistryIds(profileId: Id): Promise<Id[]> {
  const today = toDateInputValue(new Date());

  const { data, error } = await supabase
    .from('memberships')
    .select('ministry_id')
    .eq('profile_id', profileId)
    .in('role', ['leader', 'assistant'])
    .not('ministry_id', 'is', null)
    .lte('starts_on', today)
    .or(`ends_on.is.null,ends_on.gte.${today}`);
  if (error) throw error;

  return (data ?? []).flatMap((row) => (row.ministry_id ? [row.ministry_id] : []));
}

// ── A caixa de entrada (§5.1, decisão 95) ────────────────────────────────────

/**
 * O que a caixa de entrada mostra de um projeto — e nada além.
 *
 * Tipo próprio, estreito, em vez de `ProjectWithContext`: a caixa é uma coluna
 * de 320px com três linhas por item. Carregar a linha inteira só para exibir
 * título e data seria trafegar orçamento e descrição para dentro de uma lista
 * que não os mostra.
 */
export interface InboxProject {
  id: Id;
  title: string | null;
  status: ProjectStatus;
  starts_at: IsoDateTime | null;
  decided_at: IsoDateTime | null;
  ministry_name: string;
}

export interface CalendarInboxData {
  /** `changes_requested` que é seu: cada um com o botão `Resolver`. */
  pending: InboxProject[];
  /** Seus projetos decididos nos últimos 7 dias. */
  updates: InboxProject[];
  /**
   * Quantos aguardam a DECISÃO desta pessoa. `null` para quem não decide — e
   * `null`, não `0`: zero é uma fila vazia, e quem não tem fila não tem zero.
   */
  reviewQueueCount: number | null;
}

/** A régua das ATUALIZAÇÕES: é tempo, não leitura (§5.1). */
const UPDATE_WINDOW_DAYS = 7;

const INBOX_COLUMNS = 'id, title, status, starts_at, decided_at, ministry_name';

/**
 * Estreita uma linha de `projects_with_context` — view não prova coluna
 * não-nula, e uma linha sem identidade, situação ou ministério é descartada, não
 * remendada. Mesmo critério do `narrow()` de `features/projects`.
 */
function narrowInbox(row: {
  id: string | null;
  title: string | null;
  status: string | null;
  starts_at: string | null;
  decided_at: string | null;
  ministry_name: string | null;
}): InboxProject[] {
  if (!row.id || !row.status || !row.ministry_name || !isProjectStatus(row.status)) return [];
  return [
    {
      id: row.id,
      title: row.title,
      status: row.status,
      starts_at: row.starts_at,
      decided_at: row.decided_at,
      ministry_name: row.ministry_name,
    },
  ];
}

/**
 * **O que espera VOCÊ**, em uma ida ao servidor por bloco (§5.1).
 *
 * ⚠️ **O escopo é o "meu" ESTRITO** — vínculo direto de `leader`/`assistant` ou
 * autoria (spec-calendar.md §3.2) —, e não o alcance de leitura. Sem essa
 * estreitagem, um overseer abriria o calendário com a caixa cheia das pendências
 * dos outros: a RLS lhe dá a filial inteira, e "precisa de você" viraria "precisa
 * de alguém". A pendência de quem decide mora em `/analise`, e é a linha única
 * do `reviewQueueCount` que leva até lá.
 *
 * **Por que o contador da fila não vem de `useReviewQueue`:** `calendar` importa
 * de `projects`, e essa é a única direção entre features que a spec mantém
 * (§12). Uma segunda direção (`calendar → review`) por um número seria caro em
 * acoplamento e barato em benefício — e as duas contagens não podem divergir,
 * porque leem a MESMA view com a MESMA RLS. `head: true` não traz linha nenhuma.
 */
export async function fetchCalendarInbox(
  profileId: Id,
  isReviewer: boolean,
): Promise<CalendarInboxData> {
  const ministryIds = await fetchMyDirectMinistryIds(profileId);

  // `or` do PostgREST: ministério onde lidero OU projeto que escrevi. Com a
  // lista vazia (overseer sem vínculo de ministério), sobra só a autoria — e a
  // sintaxe `in.()` com lista vazia é inválida, daí o ramo.
  const mine =
    ministryIds.length > 0
      ? `ministry_id.in.(${ministryIds.join(',')}),created_by.eq.${profileId}`
      : `created_by.eq.${profileId}`;

  const since = new Date(Date.now() - UPDATE_WINDOW_DAYS * 86_400_000).toISOString();

  const [pending, updates, queue] = await Promise.all([
    supabase
      .from('projects_with_context')
      .select(INBOX_COLUMNS)
      .eq('status', 'changes_requested')
      .or(mine)
      .order('starts_at', { ascending: true, nullsFirst: false }),
    supabase
      .from('projects_with_context')
      .select(INBOX_COLUMNS)
      // ⚠️ **`changes_requested` NÃO entra aqui, e a ausência é deliberada.**
      // Todo devolvido que é seu já está no bloco de cima, com um VERBO ao lado
      // — repeti-lo como novidade diria a mesma coisa duas vezes na mesma
      // coluna, e a segunda vez sem nada para fazer a respeito. `cancelled`
      // também fica fora, por outro motivo: `cancel_project` não grava
      // `decided_at` de propósito (cancelamento não é decisão da esteira), então
      // não há como datá-lo nesta janela.
      .in('status', ['approved', 'rejected'])
      .gte('decided_at', since)
      .or(mine)
      .order('decided_at', { ascending: false }),
    isReviewer
      ? supabase.from('review_queue').select('id', { count: 'exact', head: true })
      : Promise.resolve({ count: null, error: null }),
  ]);

  if (pending.error) throw pending.error;
  if (updates.error) throw updates.error;
  if (queue.error) throw queue.error;

  return {
    pending: (pending.data ?? []).flatMap(narrowInbox),
    updates: (updates.data ?? []).flatMap(narrowInbox),
    reviewQueueCount: queue.count,
  };
}
